package com.evoapps.evoshift.domain.usecase.todo

import com.evoapps.evoshift.domain.budget.isUnfilledDemand
import com.evoapps.evoshift.domain.capacity.CapacitySummary
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.capacity.GetRosterCapacityUseCase
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.datetime.LocalDate

/**
 * SH-170: the four steps a month goes through on its way to the team: the demand (the shifts
 * without people, [RosterStatus.SKELETON]), the team's availability, the assignments and the
 * publication; [DONE] once published. SH-172: the demand and the availability run side by side,
 * in either order; [RosterProgress.stage] is the step the card's button acts on.
 */
enum class RosterStage { DEMAND, AVAILABILITY, ASSIGNMENT, PUBLICATION, DONE }

/** SH-172: where the availability step stands, whatever the demand's. */
enum class AvailabilityStep {
    /** Nobody was asked yet. */
    NOT_ASKED,
    /** Asked; answers are still missing and the deadline has not passed. */
    WAITING,
    /** Asked, and everyone answered or the deadline passed. */
    DONE,
    /** Not asked, but people were assigned directly: the step was passed over. */
    SKIPPED
}

/** Where a roster stands, with the numbers of its steps (the card's tracker). */
data class RosterProgress(
    val rosterId: String,
    val stage: RosterStage,
    /** All shifts of the month, and those nobody works yet. */
    val shifts: Int,
    val unfilled: Int,
    /** Whether availability was asked for; with it, who answered out of the team. */
    val requestSent: Boolean,
    val answered: Int? = null,
    val team: Int? = null,
    val deadline: LocalDate? = null,
    /** Those a reminder reaches (missing, with the app). */
    val remindable: List<String> = emptyList(),
    /** SH-172: the demand was approved (the month is no longer [RosterStatus.SKELETON]). */
    val demandDone: Boolean = stage.ordinal > RosterStage.DEMAND.ordinal,
    /** SH-172: the availability step on its own (it may be done before the demand). */
    val availability: AvailabilityStep = when {
        requestSent -> if (stage.ordinal > RosterStage.AVAILABILITY.ordinal) AvailabilityStep.DONE else AvailabilityStep.WAITING
        stage.ordinal > RosterStage.AVAILABILITY.ordinal -> AvailabilityStep.SKIPPED
        else -> AvailabilityStep.NOT_ASKED
    }
) {
    /** The availability step was passed without asking (people assigned directly). */
    val availabilitySkipped: Boolean get() = availability == AvailabilityStep.SKIPPED

    /**
     * SH-172: while the demand is being built, what the availability step still offers beside it:
     * asking the team (nobody asked yet) or reminding those who have not answered.
     */
    val availabilityAlongside: AvailabilityStep?
        get() = if (stage == RosterStage.DEMAND && (availability == AvailabilityStep.NOT_ASKED || availability == AvailabilityStep.WAITING)) availability else null
}

object RosterProgressRule {
    /**
     * [roster]'s stage from its status, shifts and (when asked for) availability answers. The
     * demand and the availability are two steps in either order (SH-172): a month still being
     * shaped ([RosterStatus.SKELETON]) is at the demand, whether or not availability was asked
     * (the tracker shows that step's own state beside it); a draft waits for availability until
     * it was asked for and answered (or its deadline passed), unless people are assigned already;
     * then for its unfilled shifts; then for publishing.
     */
    fun of(roster: Roster, shifts: List<Shift>, capacity: CapacitySummary?, today: LocalDate): RosterProgress {
        val unfilled = shifts.count { it.isUnfilledDemand }
        val requestSent = roster.availabilityRequestId != null
        val deadline = capacity?.request?.dueDate?.take(10)?.let { runCatching { LocalDate.parse(it) }.getOrNull() }
        val team = capacity?.people?.size
        val answered = capacity?.let { it.people.size - it.missing.size }
        val anyAssigned = shifts.any { it.userId != null }
        val answersMissing = when {
            capacity == null -> false
            deadline != null && deadline < today -> false
            else -> capacity.missing.isNotEmpty()
        }
        val availability = when {
            !requestSent -> if (anyAssigned) AvailabilityStep.SKIPPED else AvailabilityStep.NOT_ASKED
            answersMissing -> AvailabilityStep.WAITING
            else -> AvailabilityStep.DONE
        }
        val stage = when (roster.status) {
            RosterStatus.PUBLISHED, RosterStatus.LOCKED, RosterStatus.ARCHIVED -> RosterStage.DONE
            RosterStatus.SKELETON -> RosterStage.DEMAND
            RosterStatus.DRAFT -> {
                val waitingForAnswers = !anyAssigned && (availability == AvailabilityStep.NOT_ASKED || availability == AvailabilityStep.WAITING)
                when {
                    waitingForAnswers -> RosterStage.AVAILABILITY
                    unfilled > 0 || shifts.isEmpty() -> RosterStage.ASSIGNMENT
                    else -> RosterStage.PUBLICATION
                }
            }
        }
        val done = stage == RosterStage.DONE
        return RosterProgress(
            rosterId = roster.id,
            stage = stage,
            shifts = shifts.size,
            unfilled = unfilled,
            requestSent = requestSent,
            answered = answered,
            team = team,
            deadline = deadline,
            remindable = capacity?.remindable?.map { it.userId }.orEmpty(),
            demandDone = roster.status != RosterStatus.SKELETON,
            // Past the availability step in a draft: what remains of it is done (or was skipped).
            availability = when {
                done -> if (requestSent) AvailabilityStep.DONE else AvailabilityStep.SKIPPED
                stage.ordinal > RosterStage.AVAILABILITY.ordinal && availability == AvailabilityStep.WAITING -> AvailabilityStep.DONE
                else -> availability
            }
        )
    }
}

/**
 * SH-170: [RosterProgressRule] over the roster's shifts and, when availability was asked for, the
 * answers (the capacity's, without pay terms). Managers who build rosters may read both.
 */
class GetRosterProgressUseCase(
    private val shiftRepository: ShiftRepository,
    private val getCapacity: GetRosterCapacityUseCase? = null,
    private val today: () -> LocalDate = { AppClock.today() }
) {
    suspend operator fun invoke(roster: Roster): Result<RosterProgress> = runCatching {
        val done = roster.status == RosterStatus.PUBLISHED || roster.status == RosterStatus.LOCKED || roster.status == RosterStatus.ARCHIVED
        val shifts = if (done) emptyList() else shiftRepository.getShiftsForRoster(roster.id).getOrThrow()
        // SH-172: the answers count while the demand is still shaped too (asked before it).
        val capacity = if (roster.availabilityRequestId != null && (roster.status == RosterStatus.DRAFT || roster.status == RosterStatus.SKELETON)) {
            getCapacity?.invoke(roster.id, readPayTerms = false)?.getOrNull()
        } else null
        RosterProgressRule.of(roster, shifts, capacity, today())
    }
}
